import React, { useEffect, useState } from 'react';
import T from 'i18n-react';

import { InputWithSelect } from '@motorway/motorway-storybook-cra';

import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerAccountDateFilterClicked, triggerAccountPageSearchInputEvent } from 'Services/analytics/events';
import { AccountDateRange } from 'Types/account';
import { applyCypressData, isString } from 'Utilities/helpers';
import useKeywordSearch from 'Utilities/hooks/useKeywordSearch';

import { useDateFilterInAccountSearchBar } from './AccountSearchBar.helpers';

import styles from './AccountSearchBar.module.scss';

T.setTexts(require('Components/texts.json'));

const dateRanges = [
	AccountDateRange.ALL,
	AccountDateRange.TODAY,
	AccountDateRange.YESTERDAY,
	AccountDateRange.LAST_7_DAYS,
	AccountDateRange.LAST_30_DAYS,
];

const dateRangeOptions = dateRanges.map((key) => ({
	name: T.translate(`account.dateRangeFilter.${key}`),
	value: key,
}));

const AccountSearchBar = () => {
	const { category, dateRangeType, keyword, slug } = useAccountRouter();

	const shouldDisplayDateRangeFilter = useDateFilterInAccountSearchBar();

	const [dateRangeSelectOptions, setDateRangeSelectOptions] = useState([]);
	const {
		maxWidth: { breakpointTablet: isTabletOrSmaller },
	} = useBreakpoint();

	const { changePage } = useAccountRouter();

	const { onClear, onSearch, reset, searchValue } = useKeywordSearch({
		initialKeyword: keyword,
		searchFunction: ({ keyword: searchKeyword }) => {
			const trimmedSearchKeyword = isString(searchKeyword) ? searchKeyword.trim() : searchKeyword;
			changePage({ keyword: trimmedSearchKeyword });
			triggerAccountPageSearchInputEvent({ searchTerm: trimmedSearchKeyword, slug });
		},
	});

	useEffect(() => {
		if (keyword) {
			triggerAccountPageSearchInputEvent({ searchTerm: keyword, slug });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slug, category]);

	const onSearchBarClear = () => {
		onClear();
		changePage({ keyword: undefined });
	};

	const onDateRangeFilterChange = (value) => {
		changePage({ dateRangeType: value });

		triggerAccountDateFilterClicked({ filter: value, slug });
	};

	useEffect(() => {
		const dateOptions = shouldDisplayDateRangeFilter ? dateRangeOptions : [];
		setDateRangeSelectOptions(dateOptions);
	}, [shouldDisplayDateRangeFilter]);

	// Temporary effect syncing url keyword param value with input value, since InputWIthSelect is not controlled
	// component. We will delete AccountSearchBar while working on iteration 3.
	useEffect(() => {
		if (keyword !== searchValue) {
			changePage({ keyword: searchValue });
		}
	}, [category, slug]);

	useEffect(
		() => () => {
			reset();
		},
		[],
	);

	return (
		<div className={styles.accountSearchBar} {...applyCypressData(cypressIds.accountFilters.search)}>
			<InputWithSelect
				className={styles.inputWithSelect}
				inputConfig={{
					initialValue: searchValue,
					inputProps: {
						id: 'accountSearch',
						input: {
							autoComplete: 'off',
							name: 'accountSearch',
							onKeyUp: (e) => {
								onSearch(e.target.value);
							},
							placeholder: T.translate('account.search.placeholder', {
								section: T.translate(`account.sidebar.sectionLinks.${slug}`),
							}),
							value: keyword,
						},
					},
				}}
				onClearInput={onSearchBarClear}
				onSelectChange={(value) => onDateRangeFilterChange(value)}
				options={dateRangeSelectOptions}
				selectConfig={{
					rightIcon: isTabletOrSmaller ? 'filter' : 'select',
					value: dateRangeType,
				}}
				shouldHideSelectText={isTabletOrSmaller}
			/>
		</div>
	);
};

export default AccountSearchBar;
